import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBackRounded, CheckCircleRounded, ContentCopyRounded, LockRounded } from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';
import { editionConfig, firmProfile } from '../edition';
import { claimWorkspace, getInvitation, recoverAccount, register } from '../features/account/api';

const headings = {
  register: ['Create the NEO Legal workspace', 'The first account is the NEO firm administrator. Add every other staff member by invitation.'],
  invite: ['Join the NEO Legal workspace', 'Create your own password for the NEO firm account that invited you.'],
  claim: ['Claim the NEO Legal workspace', 'This is the one-time switch from the earlier OpenAI identity login to a normal NEO workspace account.'],
  recover: ['Recover your NEO Legal account', 'Use the recovery code you saved when the account was created.'],
};

export default function AccountAccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';
  const mode = useMemo(() => {
    if (location.pathname === '/recover') return 'recover';
    if (location.pathname === '/claim-account') return 'claim';
    return inviteToken ? 'invite' : 'register';
  }, [inviteToken, location.pathname]);
  const [form, setForm] = useState({ name: '', email: '', firmName: firmProfile.legalName, jurisdiction: firmProfile.jurisdiction, password: '', confirm: '', recoveryCode: '' });
  const [invitation, setInvitation] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(mode === 'invite');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mode !== 'invite') return;
    let active = true;
    getInvitation(inviteToken)
      .then(body => {
        if (!active) return;
        setInvitation(body.invitation);
        setForm(current => ({ ...current, name: body.invitation.name || '', email: body.invitation.email || '' }));
      })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoadingInvite(false); });
    return () => { active = false; };
  }, [inviteToken, mode]);

  const update = key => event => setForm(current => ({ ...current, [key]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      let result;
      if (mode === 'claim') result = await claimWorkspace({ password: form.password });
      else if (mode === 'recover') result = await recoverAccount({ email: form.email, recoveryCode: form.recoveryCode, password: form.password });
      else result = await register({ ...form, inviteToken: mode === 'invite' ? inviteToken : undefined });
      setRecoveryCode(result.recoveryCode);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function copyRecovery() {
    await navigator.clipboard.writeText(recoveryCode);
    setCopied(true);
  }

  if (recoveryCode) {
    return (
      <AccessShell>
        <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center' }}>
          <CheckCircleRounded color="success" sx={{ fontSize: 52 }} />
          <Typography variant="h3">Your NEO Legal account is ready</Typography>
          <Typography color="text.secondary">Save this recovery code now. It is shown once and is the secure fallback if you forget your password.</Typography>
        </Stack>
        <Box sx={{ my: 2.5, p: 2, borderRadius: 2.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" color="text.secondary">ONE-TIME RECOVERY CODE</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: { xs: 14, sm: 17 }, fontWeight: 800, overflowWrap: 'anywhere', my: 0.7 }}>{recoveryCode}</Typography>
          <Button size="small" startIcon={<ContentCopyRounded />} onClick={copyRecovery}>{copied ? 'Copied' : 'Copy code'}</Button>
        </Box>
        <Alert severity="warning" sx={{ mb: 2.5 }}>Lex Maple stores only a one-way fingerprint of this code. Keep it in your password manager; the NEO firm administrator cannot view it later.</Alert>
        <Button fullWidth size="large" variant="contained" onClick={() => navigate('/dashboard', { replace: true })}>I saved it — open my workspace</Button>
      </AccessShell>
    );
  }

  if (loadingInvite) return <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}><CircularProgress aria-label="Checking invitation" /></Box>;
  const [title, subtitle] = headings[mode];

  return (
    <AccessShell>
      <Button component={RouterLink} to="/" startIcon={<ArrowBackRounded />} sx={{ alignSelf: 'flex-start', ml: -1, mb: 1 }}>Back to sign in</Button>
      <Typography variant="h3">{title}</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.7, mb: 2.5 }}>{subtitle}</Typography>
      {mode === 'invite' && invitation && <Alert severity="info" sx={{ mb: 2 }}>You are joining <strong>{invitation.firmName}</strong> as {invitation.professionalTitle || invitation.role}.</Alert>}
      {mode === 'claim' && <Alert severity="info" icon={<LockRounded />} sx={{ mb: 2 }}>OpenAI identity is used only once here to prove ownership of the earlier workspace. Future sign-ins use this NEO workspace password.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={submit}>
        <Stack spacing={1.6}>
          {!['claim', 'recover'].includes(mode) && <TextField label="Full name" value={form.name} onChange={update('name')} required autoFocus />}
          {mode === 'register' && <TextField label="Firm name" value={form.firmName} InputProps={{ readOnly: true }} required />}
          {mode === 'register' && (
            <TextField select label="Firm coverage" value={form.jurisdiction} onChange={update('jurisdiction')}>
              <MenuItem value="Ontario">Ontario</MenuItem>
            </TextField>
          )}
          {!['claim'].includes(mode) && <TextField label="Work email" type="email" value={form.email} onChange={update('email')} disabled={mode === 'invite'} required autoComplete="username" />}
          {mode === 'recover' && <TextField label="Recovery code" value={form.recoveryCode} onChange={update('recoveryCode')} required autoComplete="off" />}
          <TextField label={mode === 'recover' ? 'New password' : 'Password'} type="password" value={form.password} onChange={update('password')} required autoComplete="new-password" helperText="At least 12 characters, including a letter and number." />
          <TextField label="Confirm password" type="password" value={form.confirm} onChange={update('confirm')} required autoComplete="new-password" />
          <Button type="submit" size="large" variant="contained" disabled={submitting || form.password.length < 12 || form.password !== form.confirm || (mode === 'invite' && !invitation)}>
            {submitting ? 'Securing account…' : mode === 'recover' ? 'Reset password' : mode === 'claim' ? 'Claim workspace' : mode === 'invite' ? 'Join firm workspace' : 'Create firm workspace'}
          </Button>
        </Stack>
      </Box>
      <Divider sx={{ my: 2.3 }} />
      <Typography variant="caption" color="text.secondary">By continuing, you confirm that firm administrators control membership and that client information must be handled under your professional, privacy, and security obligations.</Typography>
      {mode === 'recover' && <Typography variant="body2" sx={{ mt: 1.5 }}>Do not have the recovery code? Ask your firm administrator to help verify and restore access.</Typography>}
    </AccessShell>
  );
}

function AccessShell({ children }) {
  return (
    <Box component="main" sx={{ minHeight: '100dvh', bgcolor: 'background.default', display: 'grid', placeItems: 'center', p: { xs: 1.5, sm: 3 } }}>
      <Card sx={{ width: '100%', maxWidth: 530, p: { xs: 2.5, sm: 4 }, my: 2, borderRadius: 4, boxShadow: '0 24px 70px rgba(16,42,67,.1)' }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.4 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 6px 18px rgba(25,54,116,.12)' }}><Logo size={39} /></Box>
          <Box><Typography sx={{ fontWeight: 800, fontSize: 18 }}>{editionConfig.productName}</Typography><Typography variant="caption" color="text.secondary">Private Ontario firm access · Powered by {editionConfig.platformName}</Typography></Box>
        </Stack>
        {children}
      </Card>
    </Box>
  );
}
